import { useMounted } from '@mantine/hooks';
import type { NavigateOptions } from '@tanstack/react-router';
import { functionalUpdate } from '@tanstack/react-router';
import type {
	ColumnFilter,
	ColumnFiltersState,
	PaginationState,
	RowData,
	SortingState,
	Updater,
} from '@tanstack/table-core';
import { useCallback, useMemo, useState } from 'react';
import { type State, useTableSearchParams } from 'tanstack-table-search-params';

import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '@apple/utils/pagination/models';

import { type DataTableState, emptyTableState, type FieldMap } from '../types';
import { getColumnFiltersFromFilterData, getFilterDataFromColumnFilters } from '../utils/filters';

interface Props<TRow extends RowData, TFilters> {
	search: Record<string, unknown | undefined>;
	navigate: (params: NavigateOptions) => Promise<void>;
	defaultFilters?: TFilters;
	searchFilters?: ColumnFilter[];
	defaultSorting?: SortingState;
	defaultPageSize?: number;
	fieldMap: FieldMap<TRow, TFilters>[];
	debounceMilliseconds?:
		| {
				[KEY in keyof State]?: number;
		  }
		| number;
}

export type UseTableStateReturnType<TRow extends RowData, TFilters> = ReturnType<
	typeof useTableState<TRow, TFilters>
>;

export function useTableState<TRow extends RowData, TFilters>({
	search,
	navigate,
	defaultFilters,
	searchFilters = [],
	defaultPageSize = DEFAULT_PAGE_SIZE,
	defaultSorting = [],
	fieldMap,
	debounceMilliseconds,
}: Props<TRow, TFilters>) {
	const isMounted = useMounted();
	const defaultColumnFilters = useMemo(() => {
		return !defaultFilters
			? []
			: getColumnFiltersFromFilterData<TRow, TFilters>(defaultFilters, fieldMap);
	}, [defaultFilters, fieldMap]);

	const initialState = useMemo(
		() => getDefaults(defaultColumnFilters, defaultSorting, defaultPageSize),
		[defaultColumnFilters, defaultSorting, defaultPageSize],
	);

	const defaultState = useMemo<DataTableState>(() => {
		return {
			...initialState,
			columnFilters: [...initialState.columnFilters, ...searchFilters],
		};
	}, [initialState, searchFilters]);

	const stateAndOnChanges = useTableSearchParams(
		{
			replace: url => {
				const searchParams = new URLSearchParams(url.split('?')[1] ?? '');
				void navigate({
					search: Object.fromEntries(
						searchParams.entries().filter(x => !searchFilters.some(f => f.id === x[0])),
					),
					replace: true,
				});
			},
			// TODO - only stringified param values can be passed to query
			query: search as unknown as Record<string, string | undefined>,
			pathname: location.pathname,
		},
		{
			defaultValues: defaultState,
			debounceMilliseconds: debounceMilliseconds ?? {
				pagination: 1000,
			},
		},
	);

	const [state, setState] = useState<DataTableState>({
		...emptyTableState,
		...stateAndOnChanges.state,
	});

	const currentFilterData = useMemo(
		() => getFilterDataFromColumnFilters<TRow, TFilters>(state.columnFilters, fieldMap),
		[state.columnFilters, fieldMap],
	);

	const resetPagination = useCallback(() => {
		stateAndOnChanges.onPaginationChange?.({
			...(stateAndOnChanges.state.pagination ?? { pageSize: DEFAULT_PAGE_SIZE }),
			pageIndex: DEFAULT_PAGE_INDEX,
		});
	}, [stateAndOnChanges]);

	const onPaginationChange = useCallback(
		(valueOrUpdater: Updater<PaginationState>) => {
			if (!isMounted) {
				return;
			}

			setState(prev => ({
				...prev,
				pagination: functionalUpdate(valueOrUpdater, prev.pagination),
			}));
			stateAndOnChanges.onPaginationChange?.(valueOrUpdater);
		},
		[isMounted, stateAndOnChanges],
	);

	const onColumnFiltersChange = useCallback(
		(valueOrUpdater: Updater<ColumnFiltersState>) => {
			setState(prev => ({
				...prev,
				columnFilters: functionalUpdate(valueOrUpdater, prev.columnFilters),
				pagination: {
					...prev.pagination,
					pageIndex: DEFAULT_PAGE_INDEX, // Reset to first page
				},
			}));
			stateAndOnChanges.onColumnFiltersChange?.(valueOrUpdater);
			resetPagination();
		},
		[stateAndOnChanges, resetPagination],
	);

	const onSortingChange = useCallback(
		(valueOrUpdater: Updater<SortingState>) => {
			setState(prev => ({
				...prev,
				sorting: functionalUpdate(valueOrUpdater, prev.sorting),
			}));
			stateAndOnChanges.onSortingChange?.(valueOrUpdater);
		},
		[stateAndOnChanges],
	);

	return {
		initialState,
		state,
		currentFilterData,
		onPaginationChange,
		onColumnFiltersChange,
		onSortingChange,
	};
}

function getDefaults(
	defaultColumnFilters: ColumnFilter[],
	defaultSorting: SortingState,
	pageSize: number,
): DataTableState {
	return {
		pagination: {
			pageIndex: DEFAULT_PAGE_INDEX,
			pageSize: pageSize,
		},
		globalFilter: '',
		columnFilters: defaultColumnFilters,
		sorting: defaultSorting,
	};
}
